// ═══════════════════════════════════════════════════════════════
// ContactReportsSection.jsx
// Path: src/components/donorDetail/ContactReportsSection.jsx
//
// The "Contact Reports" tab content on the donor detail view.
// Two modes:
//   - history: chronological list of past reports for this donor + "+ New" button
//   - form:    the comprehensive ContactReportForm
//
// Loads contact reports ON DEMAND when the section mounts (mirrors the
// giftsService pattern). Filing a report auto-creates a linked contact_log
// entry and a director_tasks row via contactReportsService.create().
//
// Phase 3 of CRM_Donor_Contact_Reports_Blueprint_v1_0.md
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  FileText,
  Calendar,
  ArrowRight,
  Notebook,
  Trash2,
  Pencil,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { contactReportsService } from "../../services/contactReportsService";
import { MEETING_TYPES } from "../../constants/contactReportSections";
import { ActionButton } from "../ui/ActionButton";
import { ConfirmDialog } from "../ui/Modal";
import { ContactReportForm } from "./ContactReportForm";

// ── Format helpers ───────────────────────────────────────────
const formatDate = (isoDate) => {
  if (!isoDate) return "";
  const d = new Date(isoDate + "T12:00:00");
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const meetingTypeLabel = (value) => {
  const found = MEETING_TYPES.find((t) => t.value === value);
  return found ? found.label : value;
};

const meetingTypeColor = (value) => {
  switch (value) {
    case "discovery":    return "#3b82f6"; // blue
    case "cultivation":  return "#10b981"; // green
    case "solicitation": return "#f59e0b"; // amber
    case "stewardship":  return "#8b5cf6"; // purple
    default:             return "#6b7280"; // gray
  }
};

const truncate = (text, max) => {
  if (!text) return "";
  if (text.length <= max) return text;
  return text.slice(0, max).trim() + "…";
};

// ═══════════════════════════════════════════════════════════════
// ContactReportsSection
// ═══════════════════════════════════════════════════════════════

export const ContactReportsSection = ({ member, palette, canEdit }) => {
  const {
    userProfile,
    isAdmin,
    canFileContactReports,
    showMessage,
    refreshMember,
    bumpDataVersion,
    addTask,
    setPrintContactReportId,
  } = useApp();

  const [mode, setMode] = useState("history"); // "history" | "form"
  const [editingReport, setEditingReport] = useState(null); // null = create mode
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Permission check: admin can edit any report; director can edit their own.
  // Mirrors the RLS UPDATE policy from migration 003.
  const canEditReport = (report) => {
    if (!report) return false;
    if (isAdmin) return true;
    return report.createdBy === userProfile?.id;
  };

  // ── Load reports for this member ───────────────────────────
  const loadReports = useCallback(async () => {
    if (!member?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await contactReportsService.getByMember(member.id);
      setReports(data);
    } catch (err) {
      setError("Could not load contact reports — " + (err.message || "unknown error"));
    } finally {
      setLoading(false);
    }
  }, [member?.id]);

  useEffect(() => {
    loadReports();
    setMode("history");
    setEditingReport(null);
  }, [loadReports]);

  // ── Permission gate ────────────────────────────────────────
  if (!canFileContactReports) {
    return (
      <div
        style={{
          padding: 20,
          color: palette.text3,
          fontStyle: "italic",
          fontSize: FONT_SIZES.sm,
          fontFamily: FONT_FAMILY,
        }}
      >
        You do not have permission to view contact reports.
      </div>
    );
  }

  // ── Save handler (handles both create and edit modes) ──────
  const handleSave = async (reportData, pins = {}) => {
    if (!member?.id || !userProfile?.id) {
      showMessage("Cannot save — missing donor or user context", "error");
      return;
    }
    setSaving(true);
    try {
      if (editingReport) {
        // ── EDIT MODE ──
        // Update the report fields only. The linked contact_log entry,
        // the auto-created task, and pinned_intelligence are all left alone
        // (per blueprint §6.2 — sync-back is a v2 feature). Pins UI is
        // hidden during edit so `pins` will be all-false anyway.
        await contactReportsService.update(editingReport.id, reportData);

        // Refresh donor record (in case the contact log preview text needs to reflect
        // the updated summary — though we don't actually update the log row, this is
        // future-proof and triggers a re-render on detailForm consumers).
        refreshMember(member.id);
        bumpDataVersion();

        await loadReports();
        setEditingReport(null);
        setMode("history");
        showMessage("Contact report updated", "success");
        return;
      }

      // ── CREATE MODE ──
      // Step A: write the contact report + linked log + pins via the service.
      const { report: createdReport, warnings } =
        await contactReportsService.create(
          member.id,
          reportData,
          pins,
          {
            createdBy: userProfile.id,
            pinnedByName: userProfile.full_name || "",
          }
        );

      // Step B: auto-create the follow-up task via AppContext.addTask so the
      // directorTasks state updates and the drawer shows it immediately.
      // Non-blocking — if it fails, the contact report still stands.
      if (reportData.nextStep && reportData.nextStepDate) {
        try {
          await addTask({
            memberId: member.id,
            directorName: userProfile.full_name || "",
            title: reportData.nextStep,
            notes: `Auto-created from contact report on ${reportData.visitDate}`,
            category: "follow_up",
            priority: "normal",
            status: "open",
            dueDate: reportData.nextStepDate,
            isAuto: true,
            sourceReportId: createdReport.id,
          });
        } catch (taskErr) {
          warnings.push(
            "The contact report was saved but the follow-up task could not be created."
          );
        }
      }

      // Refresh donor record so the new linked contact_log shows in Comms history
      refreshMember(member.id);
      bumpDataVersion();

      // Reload our own list
      await loadReports();
      setMode("history");

      if (warnings && warnings.length > 0) {
        showMessage(
          "Contact report saved with warnings: " + warnings.join(" "),
          "warning"
        );
      } else {
        showMessage("Contact report saved", "success");
      }
    } catch (err) {
      showMessage(
        (editingReport ? "Could not update" : "Could not save") +
          " contact report — " +
          (err.message || "unknown error"),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  // ── Delete handler ─────────────────────────────────────────
  // Admin only. The DB FK on director_tasks.source_report_id is ON DELETE
  // SET NULL, so the auto-task survives but loses its source link. The
  // linked contact_log row also survives. Pinned items in
  // members.pinned_intelligence keep their last-known content.
  const handleDelete = async (reportId) => {
    if (!isAdmin) return;
    setDeleting(true);
    try {
      await contactReportsService.remove(reportId);
      setReports((prev) => prev.filter((r) => r.id !== reportId));
      setConfirmDeleteId(null);
      showMessage("Contact report deleted", "warning");
    } catch (err) {
      showMessage(
        "Could not delete contact report — " + (err.message || "unknown error"),
        "error"
      );
    } finally {
      setDeleting(false);
    }
  };

  // ── Render: form mode ──────────────────────────────────────
  if (mode === "form") {
    return (
      <ContactReportForm
        key={editingReport ? `edit-${editingReport.id}` : "new"}
        palette={palette}
        onSave={handleSave}
        onCancel={() => {
          setMode("history");
          setEditingReport(null);
        }}
        saving={saving}
        initialReport={editingReport}
      />
    );
  }

  // ── Render: history mode ───────────────────────────────────
  return (
    <div>
      {/* Header bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: FONT_SIZES.md,
              fontWeight: 700,
              color: palette.text1,
              fontFamily: FONT_FAMILY,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Contact Reports
          </h3>
          <p
            style={{
              margin: "2px 0 0",
              fontSize: FONT_SIZES.xs,
              color: palette.text3,
              fontFamily: FONT_FAMILY,
            }}
          >
            {reports.length === 0
              ? "No reports filed yet"
              : `${reports.length} report${reports.length === 1 ? "" : "s"} on file`}
          </p>
        </div>
        <ActionButton
          label="New Contact Report"
          icon={Plus}
          onClick={() => setMode("form")}
        />
      </div>

      {/* Loading / error / empty / list */}
      {loading && (
        <div
          style={{
            padding: 20,
            textAlign: "center",
            color: palette.text3,
            fontSize: FONT_SIZES.sm,
            fontFamily: FONT_FAMILY,
          }}
        >
          Loading…
        </div>
      )}

      {error && (
        <div
          style={{
            padding: "12px 16px",
            marginBottom: 12,
            borderRadius: 8,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#991b1b",
            fontSize: FONT_SIZES.sm,
            fontFamily: FONT_FAMILY,
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && reports.length === 0 && (
        <div
          style={{
            padding: "32px 24px",
            textAlign: "center",
            background: palette.cardBg,
            border: `1px dashed ${palette.cardBorder}`,
            borderRadius: 12,
          }}
        >
          <Notebook
            size={ICON_SIZES.xxl}
            style={{ color: palette.text3, marginBottom: 12 }}
          />
          <p
            style={{
              margin: "0 0 6px",
              fontSize: FONT_SIZES.md,
              fontWeight: 600,
              color: palette.text2,
              fontFamily: FONT_FAMILY,
            }}
          >
            No contact reports yet
          </p>
          <p
            style={{
              margin: "0 0 16px",
              fontSize: FONT_SIZES.sm,
              color: palette.text3,
              fontFamily: FONT_FAMILY,
            }}
          >
            File a contact report after every donor visit to build the institutional record.
          </p>
          <ActionButton
            label="File First Contact Report"
            icon={Plus}
            onClick={() => setMode("form")}
          />
        </div>
      )}

      {!loading && !error && reports.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {reports.map((report) => {
            const typeColor = meetingTypeColor(report.meetingType);
            return (
              <div
                key={report.id}
                onClick={() => setPrintContactReportId(report.id)}
                title="Click to view full report"
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr auto",
                  gap: 16,
                  alignItems: "start",
                  padding: "14px 16px",
                  borderRadius: 10,
                  background: palette.cardBg,
                  border: `1px solid ${palette.cardBorder}`,
                  borderLeft: `4px solid ${typeColor}`,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `${palette.accent1}06`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = palette.cardBg;
                }}
              >
                {/* Date column */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    minWidth: 90,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      fontSize: FONT_SIZES.xs,
                      color: palette.text3,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    <Calendar size={ICON_SIZES.xs} />
                    {formatDate(report.visitDate)}
                  </div>
                  <span
                    style={{
                      marginTop: 4,
                      padding: "2px 8px",
                      borderRadius: 4,
                      background: `${typeColor}18`,
                      color: typeColor,
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    {meetingTypeLabel(report.meetingType)}
                  </span>
                </div>

                {/* Body column */}
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: FONT_SIZES.sm,
                      color: palette.text1,
                      fontFamily: FONT_FAMILY,
                      lineHeight: 1.45,
                      marginBottom: 6,
                    }}
                  >
                    {truncate(report.summary, 220)}
                  </div>
                  {report.attendees && (
                    <div
                      style={{
                        fontSize: FONT_SIZES.xs,
                        color: palette.text3,
                        fontFamily: FONT_FAMILY,
                        marginBottom: 6,
                      }}
                    >
                      Attendees: {report.attendees}
                    </div>
                  )}
                  {report.nextStep && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: FONT_SIZES.xs,
                        color: palette.text2,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      <ArrowRight size={ICON_SIZES.xs} style={{ color: palette.accent1 }} />
                      <span>
                        <strong>Next:</strong> {report.nextStep}
                        {report.nextStepDate && (
                          <span style={{ color: palette.text3 }}>
                            {" "}
                            (due {formatDate(report.nextStepDate)})
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                </div>

                {/* Action column — edit (admin or own) and admin-only delete */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 6,
                  }}
                >
                  {canEditReport(report) && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingReport(report);
                        setMode("form");
                      }}
                      title="Edit contact report"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 30,
                        height: 30,
                        borderRadius: 6,
                        border: `1px solid ${palette.cardBorder}`,
                        background: "transparent",
                        color: palette.text2,
                        cursor: "pointer",
                        transition: "all 0.15s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `${palette.accent1}10`;
                        e.currentTarget.style.borderColor = palette.accent1;
                        e.currentTarget.style.color = palette.accent1;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.borderColor = palette.cardBorder;
                        e.currentTarget.style.color = palette.text2;
                      }}
                    >
                      <Pencil size={ICON_SIZES.sm} />
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDeleteId(report.id);
                      }}
                      title="Delete contact report"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 30,
                        height: 30,
                        borderRadius: 6,
                        border: `1px solid ${palette.cardBorder}`,
                        background: "transparent",
                        color: "#dc2626",
                        cursor: "pointer",
                        transition: "all 0.15s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#fef2f2";
                        e.currentTarget.style.borderColor = "#fecaca";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.borderColor = palette.cardBorder;
                      }}
                    >
                      <Trash2 size={ICON_SIZES.sm} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Delete confirmation ─────────────────────────────── */}
      {confirmDeleteId && (
        <ConfirmDialog
          title="Delete Contact Report?"
          message="This permanently removes the contact report. The linked communication log entry and any auto-created follow-up task will be kept (the task simply loses its link back to this report). Pinned donor notes also remain. Continue?"
          confirmLabel={deleting ? "Deleting..." : "Delete"}
          variant="danger"
          onConfirm={() => handleDelete(confirmDeleteId)}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </div>
  );
};
